type CountryLike =
    | { slug?: string | null; name?: string | null; code?: string | null }
    | null
    | undefined;

type RegionLike =
    | { slug?: string | null; name?: string | null }
    | null
    | undefined;

type CityLike =
    | {
          slug?: string | null;
          name?: string | null;
          nameStd?: string | null;
          region?: RegionLike;
      }
    | null
    | undefined;

/**
 * Countries whose URLs carry a state between the country and the city:
 * /workspaces/united-states/california/los-angeles. Everywhere else the city
 * follows the country directly (/workspaces/egypt/cairo).
 */
const STATE_COUNTRY_CODES = new Set(['US', 'CA']);
const STATE_COUNTRY_SLUGS = new Set(['united-states', 'canada']);

/** Whether this country's workspace URLs include a state segment. */
export function hasStateLevel(country: CountryLike | string): boolean {
    if (typeof country === 'string') {
        return STATE_COUNTRY_SLUGS.has(collapseEncoding(country).trim().toLowerCase());
    }
    const code = country?.code?.trim().toUpperCase();
    if (code) return STATE_COUNTRY_CODES.has(code);
    return STATE_COUNTRY_SLUGS.has(getCountrySlug(country));
}

/**
 * Collapses accidental multi-level percent-encoding (e.g. a raw route param
 * that was already encoded before it reached us) back to plain text, so a
 * leftover literal '%' never gets re-encoded into an ever-growing %25 chain
 * by the platform's own URL encoder.
 */
export function collapseEncoding(value: string): string {
    let current = value;
    let iterations = 0;
    while (iterations < 10 && current.includes('%')) {
        let decoded: string;
        try {
            decoded = decodeURIComponent(current);
        } catch {
            break;
        }
        if (decoded === current) break;
        current = decoded;
        iterations++;
    }
    return current;
}

export function cityNameToSlug(name: string): string {
    return collapseEncoding(name.trim()).replace(/ /g, '-').toLowerCase();
}

export function getCountrySlug(country: CountryLike, fallback = ''): string {
    const fromSlug = country?.slug?.trim().toLowerCase();
    if (fromSlug) return collapseEncoding(fromSlug);
    if (country?.name) return cityNameToSlug(country.name);
    return fallback.trim().toLowerCase();
}

/** English city from API slug (360630-Cairo → cairo). Never use translated name. */
export function getCitySlug(city: CityLike, fallback = ''): string {
    const raw = city?.slug?.trim();
    if (raw) {
        const english = raw.replace(/^\d+-/, '');
        return cityNameToSlug(english || raw);
    }
    return fallback.trim().toLowerCase();
}

/**
 * Prefer API slug; fall back to numeric id (same as ListingCard) so slug-less
 * workspaces/listings never build a truncated path that drops the last segment.
 */
export function getEntitySegment(
    slug?: string | null,
    id?: string | number | null
): string {
    const fromSlug = slug?.trim();
    if (fromSlug) return collapseEncoding(fromSlug);
    if (id != null && id !== '') return String(id);
    return '';
}

export interface WorkspacePathParts {
    country?: CountryLike;
    countrySlug?: string;
    /** The city's state; read from `city.region` when not given. */
    region?: RegionLike;
    regionSlug?: string;
    city?: CityLike;
    citySlug?: string;
    workspaceSlug?: string | null;
    workspaceId?: string | number | null;
    listingSlug?: string | null;
    listingId?: string | number | null;
}

/**
 * Build an internal workspace path using API slugs (no percent-encoding):
 * workspaces/{country}[/{state}]/{city}[/{workspace}[/{listing}]]. The state
 * is only part of the path in the US and Canada (see `hasStateLevel`).
 */
export function buildWorkspacePath({
    country,
    countrySlug: countrySlugOverride,
    region,
    regionSlug: regionSlugOverride,
    city,
    citySlug: citySlugOverride,
    workspaceSlug,
    workspaceId,
    listingSlug,
    listingId
}: WorkspacePathParts): string {
    const countrySegment =
        countrySlugOverride?.trim().toLowerCase() || getCountrySlug(country);
    const citySegment =
        citySlugOverride?.trim().toLowerCase() || getCitySlug(city);
    const workspaceSegment = getEntitySegment(workspaceSlug, workspaceId);
    const listingSegment = getEntitySegment(listingSlug, listingId);

    const stateLevel = countrySlugOverride
        ? hasStateLevel(countrySlugOverride)
        : hasStateLevel(country);
    const regionSegment =
        stateLevel && citySegment
            ? regionSlugOverride?.trim().toLowerCase() ||
              getRegionSlug(region ?? city?.region)
            : '';

    const segments = [
        'workspaces',
        countrySegment,
        regionSegment,
        citySegment
    ].filter(Boolean);

    if (workspaceSegment) segments.push(workspaceSegment);
    if (listingSegment) segments.push(listingSegment);

    return segments.join('/');
}

/** A state's URL segment: its API slug ("ile-de-france"), else its name. */
export function getRegionSlug(region: RegionLike): string {
    const fromSlug = region?.slug?.trim().toLowerCase();
    if (fromSlug) return collapseEncoding(fromSlug);
    return region?.name ? cityNameToSlug(region.name) : '';
}

/**
 * Country, state or city landing page: workspaces/{country},
 * workspaces/{country}/{state} (US and Canada only) or the city's
 * workspaces/{country}[/{state}]/{city}. '' when there is no such page.
 */
export function buildLocationPagePath({
    country,
    region,
    city
}: {
    country?: CountryLike;
    region?: RegionLike;
    city?: CityLike;
}): string {
    const countrySegment = getCountrySlug(country);
    if (!countrySegment) return '';
    if (city) return buildWorkspacePath({ country, city });
    if (!region) return `workspaces/${countrySegment}`;
    const regionSegment = hasStateLevel(country) ? getRegionSlug(region) : '';
    return regionSegment ? `workspaces/${countrySegment}/${regionSegment}` : '';
}

/**
 * Explore (map, filters, type pills) scoped to a country or city:
 * /workspaces?country=egypt&city=cairo[&{query}]. The /workspaces/{country}/…
 * paths themselves are the landing pages.
 */
export function buildExploreHref(
    { country, city }: { country?: CountryLike; city?: CityLike },
    query = ''
): string {
    const params = new URLSearchParams();
    const countrySegment = getCountrySlug(country);
    if (countrySegment) params.set('country', countrySegment);
    const citySegment = countrySegment ? getCitySlug(city) : '';
    if (citySegment) params.set('city', citySegment);
    const base = params.toString();
    return `/workspaces?${[base, query].filter(Boolean).join('&')}`;
}

export type WorkspacesPageKind =
    | 'explore'
    | 'country'
    | 'state'
    | 'city'
    | 'workspace'
    | 'listing';

/**
 * What a /workspaces pathname shows, judged by its shape: /workspaces is
 * explore, then country, [state,] city, workspace, listing. For skeletons and
 * header layout only — the route itself also recognises older links.
 */
export function workspacesPageKind(pathname: string): WorkspacesPageKind | null {
    const parts = pathname.split(/[?#]/)[0].split('/').filter(Boolean);
    if (parts[0] !== 'workspaces') return null;
    if (parts.length === 1) return 'explore';
    if (parts.length === 2) return 'country';
    const deeper: WorkspacesPageKind[] = hasStateLevel(parts[1])
        ? ['state', 'city', 'workspace', 'listing']
        : ['city', 'workspace', 'listing'];
    return deeper[parts.length - 3] ?? null;
}

function normalizePathSegment(segment: string): string {
    try {
        return decodeURIComponent(segment).toLowerCase();
    } catch {
        return segment.toLowerCase();
    }
}

export function normalizeWorkspacePath(path: string): string {
    return path
        .replace(/^\/+/, '')
        .split('/')
        .filter(Boolean)
        .map(normalizePathSegment)
        .join('/');
}

/**
 * Only redirect when we have a complete, safer canonical path.
 * Avoids redirecting to truncated paths (e.g. missing workspace segment when
 * slug is empty) which would send users to the city explore page.
 */
export function workspacePathNeedsRedirect(
    currentPath: string,
    canonicalPath: string,
    options?: { requireWorkspace?: boolean; requireListing?: boolean }
): boolean {
    const current = normalizeWorkspacePath(currentPath);
    const canonical = normalizeWorkspacePath(canonicalPath);

    if (!canonical || canonical === current) return false;

    const parts = canonical.split('/');
    // workspaces / country [/ state] / city [/ workspace [/ listing ]]
    if (parts[0] !== 'workspaces' || parts.some((part) => !part)) return false;
    const cityDepth = hasStateLevel(parts[1]) ? 4 : 3;
    if (parts.length < cityDepth) return false;

    if (options?.requireWorkspace && parts.length < cityDepth + 1) return false;
    if (options?.requireListing && parts.length < cityDepth + 2) return false;

    return true;
}

/** Absolute path for Next redirect(), optionally keeping the current query string. */
export function toWorkspaceRedirectHref(
    canonicalPath: string,
    searchParams?: Record<string, string | string[] | undefined> | null
): string {
    // Same casing as the canonical tag; encoded because redirect headers must be ASCII.
    const path = `/${canonicalPath
        .replace(/^\/+/, '')
        .split('/')
        .filter(Boolean)
        .map((segment) => encodeURIComponent(collapseEncoding(segment)))
        .join('/')}`;
    if (!searchParams) return path;

    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(searchParams)) {
        for (const item of Array.isArray(value) ? value : [value]) {
            if (item !== undefined) query.append(key, item);
        }
    }
    const queryString = query.toString();

    return queryString ? `${path}?${queryString}` : path;
}
